import type { PcrIntegration } from "./pcr";

export enum Country {
  ES = "ES",
  SE = "SE",
  NO = "NO",
  FI = "FI"
}

export type CommonProcess = {
  flowdefinitionId: "common-process";
  caseData: {
    flowId: string;
    data: CommonData;
    actions: string[];
    createdAt: string;
    flowDefinitionId: string;
    flowNumber: number;
    hasErrors: boolean;
    /** @deprecated Frozen since flow v3, which never updates it. Use `hasErrors`. */
    hasIncidents: boolean;
    incidents: any;
    numAttachments: number;
    referenceId: string;
    referenceName: string | null;
    sandbox: boolean;
    sandboxConfig: Record<string, unknown>;
    status: string;
    updatedAt: string;
    version: number;
    versionTag: string | null;
  };
};
export type CommonData = {
  flowId: string;
  originationDate: string;
  country: Country;
  businessStatus: string;
  agentApiApplication: AgentApiApplication;
  attachments: Attachments;
  queues: Queues;
  communication: any[];
  integrations: Integrations;
  calculations: any;
  offer: any;
  previousOffer?: any;
  referenceId: string;
  loanApplication?: {
    mainApplicant: PreviousLoanApplicationsOnThisNIF;
    coapplicant: PreviousLoanApplicationsOnThisNIF;
  };
  debtConsolidation?: RefinanceFormData;
  override?: Override;
  caseWork?: {
    "manual-pep-review"?: {
      isPepHitFalsePositive: boolean;
      isPepOrRCA: boolean;
      continueApplication: boolean;
      pepReviewComment: string;
    };
    "manual-changed-values": {
      firstName?: string;
      lastName?: string;
      mobileNumber?: string;
      emailAddress?: string;
      firstNameCoapplicant?: string;
      lastNameCoapplicant?: string;
      mobileNumberCoapplicant?: string;
      emailAddressCoapplicant?: string;
      accountNumber?: string;
      autogiroAccountNumber?: string;
      disbursementAccountNumber?: string;
    };
  };
  updates?: {
    allowUpdates: boolean;
    updatedValues?: UpdateApplication;
  };
  core?: any;
};

export type Input = AgentApiApplication & Override;

export type AgentApiApplication = {
  application: Application;
  applicant: Applicant;
  coapplicant?: Coapplicant;
  purchase?: Purchase[];
  process: "loan-se" | "card-se" | "sales-finance-se" | "loan-fi";
  agentId: string;
};

type Override = {
  bisnode?: {
    nationalId: string;
  };
  uc?: {
    nationalId: string;
    nationalIdCoapplicant?: string;
  };
};

type Application = {
  appliedAmount: number;
  appliedCreditLimit?: number;
  appliedRepaymentTime: number;
  appliedInterestRate?: number;
  agentName: string;
  externalApplicationId?: string;
  purpose: Purpose;
  refinanceAmount: number;
  bankAccountNumber?: string;
  product?: Product;
  additionalProduct?: Product;
  ipAddress?: string;
  device_OS?: string;
  campaignId?: string;
  upsellConsent?: boolean;
  tomBlob?: string;
};

interface Coapplicant extends Applicant {}
export interface Employment {
  type: EmploymentType;
  percentage?: number;
  employer?: string;
  position?: string;
  level?: string;
  startDate?: Date;
}

export interface Expenses {
  amount: number;
  type: ExpenseType;
  period: string;
}

export interface Income {
  amount: number;
  type: IncomeType;
  period: string;
}

interface Citizenship {
  countryCode: string;
}

interface Applicant {
  nationalId: string;
  birthDate?: Date;
  firstName: string;
  lastName: string;
  maritalStatus: MaritalStatus;
  hasActiveCollectionCase?: boolean;
  hasPaymentRemark?: boolean;
  mobileNumber: string;
  emailAddress: string;
  isPEP?: boolean;
  // Optional: not required for Finnish applicants
  // (see apps/flow-setup/setup/api-public/schemas/finland.yaml)
  numberOfChildrenBelow18?: number;
  educationLevel?: EducationLevel;
  preferredLanguage?: string;
  psd2Data?: string;
  numberOfCardsInHousehold?: number;
  dwelling: Dwelling;
  employment: Employment;
  expenses: Expenses[];
  income: Income[];
  citizenships?: Citizenship[];
  debts?: Debts[];
}

interface Purchase {
  amount?: number;
  type?: PurchaseType;
  airlinename?: string;
  hotelname?: string;
  hotelcheckindate?: Date;
  traveldate?: Date;
  returntraveldate?: Date;
  destination?: string;
  incVAT?: boolean;
  travellernames?: string[];
  billingaddress?: string;
  deliveryaddress?: string;
  orderdate?: Date;
  ordernumber?: string;
}

export type Dwelling = {
  type: DwellingType;
  startDate: Date;
};
interface Debts {
  debtType: DebtType;
  bankName?: string;
  remainingPaymentMonths?: number;
  outstandingAmount: number;
  monthlyPaymentAmount?: number;
  redeem: boolean;
  interestRate?: number;
  creditLimit?: number;
  bankAccountNumber?: string;
  cid?: string;
  percentageResponsible?: number;
}

interface Attachments {
  downPaymentPlan: any;
  loanContract: any;
  signedDocument?: any;
}

interface Queues {
  [key: string]: boolean;
}

export const OfferType = {
  ORIGINAL: "original",
  COUNTER: "counter"
} as const;

export type OfferType = (typeof OfferType)[keyof typeof OfferType];

interface BisnodeFiReport {
  response?: {
    consumerInformation?: {
      legalName?: {
        firstName?: string;
        familyName?: string;
      };
      registeredAddresses?: {
        streetAddress?: string;
        postalCode?: string;
        postOffice?: string;
      }[];
    };
  };
}

interface Integrations {
  uc: {
    addressInformation?: {
      streetName?: string;
      postcode?: string;
      municipality?: string;
      // applicant: any,
      // coapplicant: any
    };
    individualReport: any;
    debts?: {
      type: "loan" | "card";
      description: string;
      amount: number;
    }[];
  };
  tink: any;
  // Finnish Positive Credit Register. `prePayoutPcr` is the pre-payout snapshot.
  pcr?: PcrIntegration;
  prePayoutPcr?: PcrIntegration;
  statistica: any;
  cm1: any;
  signicat: SignicatState;
  dgs: any;
  leabank: any;
  experian: any;
  bisnode: {
    // Finnish DVV (population registry) report — the authoritative address
    // and name source for Finland.
    dvvFinance?: {
      applicant?: BisnodeFiReport;
      coapplicant?: BisnodeFiReport;
    };
    // Finnish payment-remark report, the fallback name source for Finland.
    paymentRemark?: {
      applicant?: BisnodeFiReport;
      coapplicant?: BisnodeFiReport;
    };
    applicant?: {
      response?: {
        consumerInformation?: {
          legalName?: {
            firstName?: string;
            familyName?: string;
          };
        };
      };
    };
    coapplicant?: {
      response?: {
        consumerInformation?: {
          legalName?: {
            firstName?: string;
            familyName?: string;
          };
        };
      };
    };
  };
  LeaBankWeb: {
    tink?: {
      incomeCheckId: string;
      consent: boolean;
    };
    customerInformation?: {
      debts?: any[];
      accountNumber?: string;
      isPEP?: boolean;
      citizenship?: string;
      appliedAmount?: number;
    };
    "insurance-sale"?: {
      Insurance: boolean;
    };
    startInformation?: {
      selectedOffer?: OfferType;
    };
  };
  itx?: ItxCall & ItxSms;
}

type ItxCall = {
  calls: any[];
  to: string;
};

export type ItxSms = {
  smsSent: any[];
  to: SendTo;
  message: string;
};

enum SendTo {
  Both = "both",
  Applicant = "applicant",
  Coapplicant = "coapplicant"
}

type DebtType =
  | "ConsumerLoan"
  | "Creditcard"
  | "MortgageLoan"
  | "StudentLoan"
  | "VehicleLoan"
  | "FlexibleMortgage"
  | "FlexibleConsumerLoan"
  | "ChargeCard"
  | "HousingCooperativeSharedDebt"
  | "Other";

type EmploymentType =
  | "Furlough"
  | "Pensioner"
  | "Incapacitated"
  | "FixedTermWorkContract"
  | "PermanentEmployment"
  | "Entrepreneur"
  | "Other"
  | "Student"
  | "Unemployed"
  | "WorkAssessmentAllowance"
  | "StayAtHome";

type DwellingType =
  | "OwnHouse"
  | "OwnApartment"
  | "RentalApartment"
  | "HousingCooperative"
  | "LivingWithParents"
  | "CompanyHousing"
  | "Other";

type MaritalStatus =
  | "Single"
  | "Married"
  | "Divorced"
  | "Widower"
  | "Separated"
  | "CoHabiting";

type EducationLevel =
  | "Bachelor"
  | "PrimarySchool"
  | "WorkEducation"
  | "Master"
  | "HighSchool";

type Purpose =
  | "Car"
  | "Boat"
  | "Caravan"
  | "MC"
  | "OtherVehicle"
  | "BusinessLoan"
  | "Refinance"
  | "Consolidation"
  | "Renovation"
  | "Investment"
  | "MedicalExpenses"
  | "VacationTravelling"
  | "Wedding"
  | "Education"
  | "Invoices"
  | "Other";

export type IncomeType =
  | "GrossIncome"
  | "NetIncome"
  | "PartnerGrossIncome"
  | "RentalIncome"
  | "InsuranceIncome"
  | "AlimonyIncome"
  | "GrossHouseholdIncome"
  | "OtherIncome";

export type ExpenseType =
  | "RentalCost"
  | "Alimony"
  | "CarLeasing"
  | "HousingExpenses"
  | "OtherExpenses";

type Product = "Loans" | "CreditCards" | "SalesFinance";

type PurchaseType = "Flight" | "Hotel" | "Ferry" | "CombinedTravel";

export type PreviousLoanApplicationsOnThisNIF = {
  totalNrOfApplications: {
    count: number;
    latestDate: string;
  };
  totalNrOfApplicationsRejected: {
    count: number;
    latestDate: string;
  };
  totalNrOfApplicationsCanceled: {
    count: number;
    latestDate: string;
  };
  totalNrOfApplicationsDisbursed: {
    count: number;
    latestDate: string;
  };
  totalNrOfApplicationsApproved: {
    count: number;
    latestDate: string;
  };
  totalNrOfApplicationsApprovedAndActive: {
    count: number;
    latestDate: string;
  };
  totalNrOfApplicationsApprovedButExpired: {
    count: number;
    latestDate: string;
  };
  manualDeclines: {
    count: number;
    latestDate: string;
  };
  hadPreviousInsurance?: boolean | null;
};

interface SignicatState {
  sign: any;
  auth: any;
}

export type RefinanceDetails = {
  accountNumber: string;
  bankName: string;
  amount: number;
  paymentReference: string;
};

// The return type for verify-refinance-documentation
export type RefinanceFormData = {
  refinanceDetails: RefinanceDetails[];
  comment?: string;
  decision: boolean | null;
  files: Attachment[];
};

export type Attachment = {
  filetype?: string;
  filename: string;
  createdAt?: string; // ISO 8601 Date string.
  attachmentId?: string;
  type?: "file" | "url";
  fileId: string;
  encoding?: string;
  mimetype?: string;
  title?: string;
  description?: string;
  attachmentKey?: string;
};

export type UpdateApplication = {
  loanAmount?: number;
  refinanceAmount?: number;
  loanTerms?: number;
  loanInterestRate?: number;
  comment?: string;
};

export const InsuranceOptions = {
  WITH_INSURANCE: "with_insurance",
  WITHOUT_INSURANCE: "without_insurance",
  NO_ANSWER: "no_answer",
  NOT_QUALIFIED: "not_qualified"
} as const;
